import Api from './apiConfig';
import { AuthService } from './AuthService';
import store from '../store/AriStore';
import { startLoading, finishLoading } from '../reducer/loading';
import { API_BASE_URL } from '../config/env';
import type { FileAttachment, FolderStatus, FileStatus } from "../file-tree-manager/types/tree-types";

// In-flight request de-duplication (prevents duplicate fetches under React.StrictMode or rapid re-renders)
const inflightRequests = new Map<string, Promise<any>>();

async function fetchWithDedupe<T>(key: string, runner: () => Promise<T>): Promise<T> {
  const existing = inflightRequests.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const promise = runner().finally(() => {
    inflightRequests.delete(key);
  });
  inflightRequests.set(key, promise as unknown as Promise<any>);
  return promise;
}

export const getIndexList = () => {
	try {
		return Api.get(`/console/indexs`);
	} catch (error) {
		console.error('Error fetching index list:', error);
		throw error;
	}
};

export const getDocumentList = (category: string) => {
	return Api.get(`/console/documents?category=${category}`);
};

export const getDocument = (documentId: string) => {
	return Api.get(`/console/document?id=${documentId}`);
};

export const updateContents = (contents: {
  categoryCode: string | undefined;
  id: string | undefined;
  sourcepage: number | undefined;
  sourcefile: string | undefined;
  content: string | undefined;
  bizTerms: string | undefined;
  altTerms: string | undefined;
  status: string | undefined;
}) => {
	return Api.post(`/console/document/markdown`, contents);
}

const authService = new AuthService();

// Contents tree node DTO
export type ContentsNodePayload = {
  id?: string;
  parentId?: string | null;
  title?: string;
  type?: 'folder' | 'file';
  status?: string;
  url?: string;
  contentsManager?: string[];
};

// Create node
export const createContentsNode = async (payload: any): Promise<any> => {
  try {
    store.dispatch(startLoading());
    const token = await authService.getAccessToken();
    const res = await fetch(`${API_BASE_URL}/contents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const message = await res.text().catch(() => '');
      throw new Error(`Failed to create contents node: ${res.status} ${message}`);
    }
    return res.json().catch(() => ({}));
  } finally {
    store.dispatch(finishLoading());
  }
};

// Update node
export const updateContentsNode = async (id: string, payload: any): Promise<any> => {
  try {
    store.dispatch(startLoading());
    const token = await authService.getAccessToken();
    const res = await fetch(`${API_BASE_URL}/contents/update/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const message = await res.text().catch(() => '');
      throw new Error(`Failed to update contents node: ${res.status} ${message}`);
    }
    return res.json().catch(() => ({}));
  } finally {
    store.dispatch(finishLoading());
  }
};

export const processingContentsNode = async (id: string, payload: any): Promise<any> => {
  try {
    store.dispatch(startLoading());
    const token = await authService.getAccessToken();
    const res = await fetch(`${API_BASE_URL}/contents/process/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const message = await res.text().catch(() => '');
      throw new Error(`Failed to update contents node: ${res.status} ${message}`);
    }
    return res.json().catch(() => ({}));
  } finally {
    store.dispatch(finishLoading());
  }
};

// Delete node
export const deleteContentsNode = async (id: string): Promise<void> => {
  try {
    store.dispatch(startLoading());
    const token = await authService.getAccessToken();
    const res = await fetch(`${API_BASE_URL}/contents/delete/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const message = await res.text().catch(() => '');
      throw new Error(`Failed to delete contents node: ${res.status} ${message}`);
    }
  } finally {
    store.dispatch(finishLoading());
  }
};

export const getContentsTree = async () => {
  return fetchWithDedupe('GET:/contents', async () => {
    try {
      store.dispatch(startLoading());
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE_URL}/contents`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const message = await res.text().catch(() => '');
        throw new Error(`Failed to fetch contents tree: ${res.status} ${message}`);
      }
      return res.json();
    } finally {
      store.dispatch(finishLoading());
    }
  });
}

// Flow config (status transition sequences)
export type FlowConfigMap = {
  file?: Record<string, FileStatus[]>;
  folder?: Record<string, FolderStatus[]>;
}
export type FlowConfigArray = {
  file?: FileStatus[];
  folder?: FolderStatus[];
}
export type FlowConfig = FlowConfigMap | FlowConfigArray

export const getFlowConfig = async (contentsId: number, status: string, type: string): Promise<FlowConfig> => {
  const key = 'GET:/contents/flow';
  return fetchWithDedupe(key, async () => {
    try {
      store.dispatch(startLoading());
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE_URL}/contents/${contentsId}/flow/${status}/${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const message = await res.text().catch(() => '');
        throw new Error(`Failed to fetch flow config: ${res.status} ${message}`);
      }
      const json = await res.json().catch(() => ({} as FlowConfig));
      return json as FlowConfig;
    } finally {
      store.dispatch(finishLoading());
    }
  });
}

// Fetch markdown/terms by attachment id
type AttachmentContentResponse = {
  attachmentId: string
  contents: string
  status: string
  fileName: string
  fileType: string
  terms: Array<{
    termsId: number | string
    terms: string
    altTerms: string | null
    descryption: string | null
    status: string | null
  }>
}

export const getAttachmentContent = async (
  attachmentId: string
): Promise<{ attachmentId: string; content: string; status: string; fileName: string; fileType: string; terms: Array<{ termsId: string; terms: string; altTerms: string[]; descryption: string | null; status: string | null }> }> => {
  const key = `GET:/contents/attachments/${encodeURIComponent(attachmentId)}/markdown`;
  return fetchWithDedupe(key, async () => {
    try {
      store.dispatch(startLoading());
      const token = await authService.getAccessToken();
      const res = await fetch(`${API_BASE_URL}/contents/attachments/${encodeURIComponent(attachmentId)}/markdown`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const message = await res.text().catch(() => '');
        throw new Error(`Failed to fetch attachment markdown: ${res.status} ${message}`);
      }
      const json = (await res.json()) as AttachmentContentResponse;
    const content: string = json?.contents ?? '';
    const status: string = json?.status ?? 'draft';
    const fileName: string = json?.fileName ?? '';
    const fileType: string = json?.fileType ?? '';
    const terms = Array.isArray(json?.terms)
      ? json.terms.map((t) => ({
          termsId: String(t.termsId),
          terms: t.terms,
          altTerms: typeof t.altTerms === 'string' && t.altTerms?.length ? t.altTerms.split(',').map((s) => s.trim()).filter((s) => s.length > 0) : [],
          descryption: t.descryption,
          status: t.status,
        }))
      : [];
      return { attachmentId: String(json?.attachmentId ?? attachmentId), content, status, terms, fileName, fileType };
    } finally {
      store.dispatch(finishLoading());
    }
  });
}

export const uploadAttachment = async (
  nodeId: string,
  file: File,
  meta: FileAttachment
): Promise<FileAttachment> => {
  store.dispatch(startLoading());
  const token = await authService.getAccessToken();

  const formData = new FormData();
  formData.append('file', file);
  // FileAttachment의 모든 속성 동반 전송 (문자열로 직렬화)
  formData.append('contentsId', String(nodeId));
  formData.append('id', String(meta.id));
  formData.append('fileName', meta.fileName);
  formData.append('fileSize', String(meta.fileSize));
  formData.append('fileType', meta.fileType);
  formData.append('uploadedAt', meta.uploadedAt instanceof Date ? meta.uploadedAt.toISOString() : String(meta.uploadedAt));

  const res = await fetch(`${API_BASE_URL}/contents/${encodeURIComponent(nodeId)}/attachments`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const message = await res.text().catch(() => '');
    store.dispatch(finishLoading());
    throw new Error(`Failed to upload attachment: ${res.status} ${message}`);
  }

  const data = await res.json().catch(() => null as unknown as any);

  if (data && (data.name || data.fileName)) {
    const mapped: FileAttachment = {
      id: String(data.id ?? meta.id),
      contentsId: data.contentsId ?? meta.contentsId,
      fileName: String(data.name ?? data.fileName ?? meta.fileName),
      fileSize: data.fileSize ?? meta.fileSize ?? "0",
      fileType: String(data.type ?? data.fileType ?? meta.fileType ?? ''),
      uploadedAt: data.uploadedAt ? new Date(data.uploadedAt) : meta.uploadedAt,
    };
    store.dispatch(finishLoading());
    return mapped;
  }

  // 응답 본문이 없거나 예상치 못한 형식인 경우, 클라이언트 메타를 그대로 반환
  store.dispatch(finishLoading());
  return meta;
}

export const deleteAttachment = async (
  id: string,
  contentsId: number,
  fileName: string
): Promise<void> => {
  store.dispatch(startLoading());

  const token = await authService.getAccessToken();

  const formData = new FormData();
  formData.append('id', id);
  formData.append('contentsId', String(contentsId));
  formData.append('fileName', fileName);

  const res = await fetch(`${API_BASE_URL}/contents/${id}/attachments/delete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    store.dispatch(finishLoading());
    throw new Error(`Failed to delete attachment: ${res.status} ${message}`);
  }
  store.dispatch(finishLoading());
}

export const downloadAttachment = async (
  id: string,
  contentsId: number,
  fileName: string
): Promise<Blob> => {
  const formData = new FormData();
  
  formData.append('id', id);
  formData.append('contentsId', String(contentsId));
  formData.append('fileName', fileName);

  store.dispatch(startLoading());
  const token = await authService.getAccessToken();
  const res = await fetch(`${API_BASE_URL}/contents/${id}/attachments/download`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    store.dispatch(finishLoading());
    throw new Error(`Failed to download attachment: ${res.status} ${message}`);
  }
  const blob = await res.blob();
  store.dispatch(finishLoading());
  return blob;
}

// Save a single term for an attachment
export const saveTerm = async (
  attachmentId: string,
  term: { termsId: string; terms: string; altTerms: string[]; descryption: string | null; status: string | null }
): Promise<void> => {
  store.dispatch(startLoading());
  const token = await authService.getAccessToken();
  const payload = {
    termsId: term.termsId,
    terms: term.terms,
    altTerms: Array.isArray(term.altTerms) ? term.altTerms.join(',') : '',
    descryption: term.descryption ?? '',
    status: term.status ?? 'draft',
  };
  const res = await fetch(`${API_BASE_URL}/contents/attachments/${encodeURIComponent(attachmentId)}/terms/${encodeURIComponent(term.termsId)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    store.dispatch(finishLoading());
    throw new Error(`Failed to save term: ${res.status} ${message}`);
  }
  store.dispatch(finishLoading());
}

// Save all terms for an attachment (bulk)
export const saveTermsBulk = async (
  payload: {
    attachmentId: string;
    terms: Array<{ termsId: string | number; terms: string; altTerms: string[] | null; descryption: string | null; status: string | null }>
  }
): Promise<void> => {
  store.dispatch(startLoading());
  const token = await authService.getAccessToken();
  const body = {
    attachmentId: String(payload.attachmentId),
    terms: payload.terms.map(t => ({
      termsId: typeof t.termsId === 'number' ? t.termsId : Number(t.termsId),
      terms: t.terms,
      altTerms: Array.isArray(t.altTerms) ? (t.altTerms.length ? t.altTerms.join(',') : null) : t.altTerms,
      descryption: t.descryption,
      status: t.status,
    })),
  };
  const res = await fetch(`${API_BASE_URL}/contents/attachments/terms`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    store.dispatch(finishLoading());
    throw new Error(`Failed to save terms: ${res.status} ${message}`);
  }
  store.dispatch(finishLoading());
}